// @ts-nocheck
import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { PlaybackTypes } from 'models/kitchenSink';

import Video from 'models/propTypes/video';
import Row from 'models/propTypes/row';
import { getPlaybackUrl, clearPlaybackUrl } from 'actions/playback';
import {
  constructGAMDAIResource,
  constructFWDAIResource,
  createNonDAIContentResource,
  createSyncbakResource,
  createDAIAPIContentResource,
} from 'support/player/resourceHelper';
import {
  getVideoMetadata,
  getPregameMetadata,
  getCarouselMetadata,
  getClickedVideoMetadata,
} from 'support/analytics/analyticsHelper';
import Tracker from 'support/analytics/Tracker';
import { useNetworkName, useFW } from 'hooks/kitchenSink';

const usePlaybackResource = ({
  videoToPlay,
  videoToDisplay,
  row: providedRow,
  isAutoplay,
  authorizedProvider,
  onError: providedOnError,
  isCarousel,
  slideNum,
}) => {
  const dispatch = useDispatch();
  const [resource, setResource] = useState(null);
  const networkName = useNetworkName(videoToPlay?.network);
  const isFreeWheel = useFW();

  const csidLive = useSelector(
    state =>
      state.kitchenSink.data?.config?.modules?.Freewheel?.params?.csid_live,
  );

  const csidVod = useSelector(
    state =>
      state.kitchenSink.data?.config?.modules?.Freewheel?.params?.csid_vod,
  );

  const contextFmsParams = useSelector(state => state.fms?.data?.fms_params);
  const dmaStation = videoToPlay?.dmaStation;
  const hasFmsParamsOrStation = contextFmsParams || dmaStation;
  const fmsParams = hasFmsParamsOrStation
    ? {
        ...(contextFmsParams || {}),
        ...(dmaStation ? { imafw_station: dmaStation } : {}),
      }
    : undefined;

  // Storing in a ref because we don't actually want video object changes to trigger
  // a resource change.
  const videoContext = {
    video: videoToPlay,
    videoNetworkName: networkName,
    displayedVideo: videoToDisplay,
    row: providedRow,
    onError: providedOnError,
    currentProvider: authorizedProvider,
    tracking: {
      autoplay: isAutoplay,
      get preGame() {
        if (videoToDisplay && videoToPlay) {
          const pregame = videoToDisplay.event?.pregame;
          const preEventStatus = videoToDisplay.event?.status;
          const displayedVideoId = videoToDisplay.id;
          const playingVideoId = videoToPlay.id;
          const preGameIsPlaying =
            pregame?.trackingId === videoToPlay.trackingId;

          const displayedVideoIsPlaying = displayedVideoId === playingVideoId;

          if (preGameIsPlaying || (displayedVideoIsPlaying && pregame)) {
            return {
              // This is only for analytics
              eventId: videoToDisplay.trackingId,
              // Need the following escape hatch to set active since the player
              // may start the active video before preEventStatus is updated
              eventStatus: displayedVideoIsPlaying ? 'active' : preEventStatus,
            };
          }
        }
        return {};
      },
    },
    freeWheel: {
      csidLive,
      csidVod,
      fmsParams,
      isFreeWheel,
    },
  };
  const videoContextRef = useRef(videoContext);
  useEffect(() => {
    videoContextRef.current = videoContext;
  });

  const lastValidVideoInfo = useRef({
    videoId: null,
    rowId: null,
    displayedVideoId: null,
  });

  const videoId = videoToPlay?.id;
  const rowId = providedRow?.id;
  const displayedVideoId = videoToDisplay?.id;

  const isTrackingDataReady = useSelector(
    state => state.analytics?.tracking?.dataHasBeenSet,
  );

  const isFMSInitialized = useSelector(state => state.fms?.initialized);

  const canConstructResource = !isFreeWheel || isFMSInitialized;

  const isCcEnabled = useSelector(state => state.closedCaptioning);

  const clickedVideoId = useSelector(state => state.clickedVideo?.videoId);
  const rowTitle = useSelector(state => state.clickedVideo?.rowTitle);
  const rowPos = useSelector(state => state.clickedVideo?.rowPos);
  const rowCol = useSelector(state => state.clickedVideo?.rowCol);

  const isClicked = videoId === clickedVideoId;

  const ks = useSelector(state => state.kitchenSink.data);
  const auth = useSelector(state => state.auth);
  const geoInfo = useSelector(state => state.geoInfo);

  const daiApiContentSourceId =
    ks.config?.modules?.prerollParams?.params?.contentSourceId;

  useEffect(() => {
    const id3OwnersSet = new Set();

    if (ks.config?.modules?.ID3GameEndTransitions?.enabled) {
      const gameEndParams = ks.config?.modules?.gameEnd?.params;
      if (gameEndParams && typeof gameEndParams === 'object') {
        Object.keys(gameEndParams).forEach(key => id3OwnersSet.add(key));
      }
    }

    if (ks.config?.modules?.ID3StreamEndTransitions?.enabled) {
      const broadcastEndParams = ks.config?.modules?.broadcastEnd?.params;
      if (broadcastEndParams && typeof broadcastEndParams === 'object') {
        Object.keys(broadcastEndParams).forEach(key => id3OwnersSet.add(key));
      }
    }

    const id3Owners = Array.from(id3OwnersSet);

    if (!isTrackingDataReady || !canConstructResource) return;

    const fetchPlaybackUrl = (video, provider) => {
      return dispatch(getPlaybackUrl(video, provider));
    };

    const createAndSetResource = async () => {
      let res;
      let stationCode;
      const {
        video,
        videoNetworkName,
        displayedVideo,
        row,
        onError,
        tracking,
        currentProvider,
        freeWheel: {
          csidLive: csidLiveVal,
          csidVod: csidVodVal,
          fmsParams: fmsParamsVal,
          isFreeWheel: isFW,
        },
      } = videoContextRef.current;
      const isPlayingSameVideo = () => {
        const {
          videoId: lastValidVideoId,
          rowId: lastValidRowId,
          displayedVideoId: lastValidDisplayedVideoId,
        } = lastValidVideoInfo.current;

        return (
          lastValidVideoId === video.id &&
          lastValidRowId === row.slug &&
          lastValidDisplayedVideoId === displayedVideo.id
        );
      };

      const vidObj = JSON.parse(JSON.stringify(video));

      if (vidObj) {
        if (isPlayingSameVideo()) return;

        if (vidObj.playback.type === PlaybackTypes.STATION_OPT_IN) {
          if (
            auth.paramountPlus.user.data !== null &&
            auth.adobe.authN !== null
          ) {
            // paramount plus logged in && adobe (mvpd) logged in
            // playbackMatrix -> both
            vidObj.playback.isSyncbak =
              ks.playback[PlaybackTypes.STATION_OPT_IN].playbackMatrix.both ===
              PlaybackTypes.SYNCBAK;
            vidObj.playback =
              ks.playback[PlaybackTypes.STATION_OPT_IN].playbackMatrix.both;
          } else if (auth.adobe.authN !== null) {
            // adobe (mvpd) logged in
            // playbackMatrix -> adobe
            vidObj.playback.isSyncbak =
              ks.playback[PlaybackTypes.STATION_OPT_IN].playbackMatrix.adobe ===
              PlaybackTypes.SYNCBAK;
            vidObj.playback =
              ks.playback[PlaybackTypes.STATION_OPT_IN].playbackMatrix.adobe;
          } else if (auth.paramountPlus.user.data !== null) {
            // paramount plus logged in
            // playbackMatrix -> paramountplus
            vidObj.playback.isSyncbak =
              ks.playback[PlaybackTypes.STATION_OPT_IN].playbackMatrix
                .paramountplus === PlaybackTypes.SYNCBAK;
            vidObj.playback.type =
              ks.playback[
                PlaybackTypes.STATION_OPT_IN
              ].playbackMatrix.paramountplus;
          } else {
            // not logged in
            // syncbak
            vidObj.playback.isSyncbak = true;
            vidObj.playback.type = PlaybackTypes.SYNCBAK;
          }
        }

        if (vidObj.ads.isDai && !vidObj.playback.isSyncbak) {
          dispatch(clearPlaybackUrl());
          if (isFW) {
            res = await constructFWDAIResource(
              vidObj,
              fmsParamsVal,
              csidLiveVal,
              id3Owners,
            );
          } else {
            res = await constructGAMDAIResource(vidObj);
          }
        } else if (vidObj.playback.isSyncbak) {
          const response = await fetchPlaybackUrl(vidObj, currentProvider);
          if (response && response.streamUrl && !response.errorCode) {
            const { streamUrl, stationCode: code } = response;
            res = createSyncbakResource(streamUrl, id3Owners);
            stationCode = code;
          } else {
            onError({ type: 'syncbak', code: response?.errorCode || null });
          }
        } else {
          // Non-DAI Content
          if (
            vidObj.properties &&
            vidObj.properties.type === 'vod' &&
            !!daiApiContentSourceId
          ) {
            res = await createDAIAPIContentResource(
              vidObj,
              daiApiContentSourceId,
              fmsParamsVal,
              csidVodVal,
            );
          } else {
            res = createNonDAIContentResource(vidObj, id3Owners);
          }
          dispatch(clearPlaybackUrl());
        }
      }

      // Attach analytics metadata and set resource
      if (res) {
        const {
          autoplay,
          preGame: { eventStatus, eventId },
        } = tracking;

        const metadata = {
          ...getVideoMetadata(
            vidObj,
            displayedVideo,
            row,
            autoplay,
            stationCode,
            videoNetworkName,
            isCcEnabled,
            isCarousel,
            geoInfo,
          ),
          ...getPregameMetadata(eventId, eventStatus),
          ...Tracker.getHbParams(),
          ...getCarouselMetadata(isCarousel, slideNum),
          ...getClickedVideoMetadata(isClicked, {
            rowTitle,
            rowPos,
            rowCol,
          }),
        };
        if (res instanceof Array) {
          res.forEach(async individualResource => {
            const r = individualResource;
            r.metadata = {
              ...individualResource.metadata,
              ...metadata,
            };
          });
        } else {
          res.metadata = {
            ...res.metadata,
            ...metadata,
          };
        }

        setResource(res);

        lastValidVideoInfo.current = {
          videoId: video.id,
          rowId: row?.slug,
          displayedVideoId: displayedVideo.id,
        };
      }
    };

    createAndSetResource();
  }, [
    dispatch,
    videoId,
    displayedVideoId,
    rowId,
    isTrackingDataReady,
    canConstructResource,
    isCcEnabled,
    isCarousel,
    slideNum,
    isClicked,
    rowTitle,
    rowPos,
    rowCol,
    ks,
    auth,
    geoInfo,
    daiApiContentSourceId,
  ]);

  return [resource];
};

const PlayerResource = ({
  render,
  onError,
  videoToPlay,
  videoToDisplay,
  row,
  isAutoplay,
  authorizedProvider,
  isCarousel,
  slideNum,
}) => {
  const [resource] = usePlaybackResource({
    videoToPlay,
    videoToDisplay,
    row,
    isAutoplay,
    authorizedProvider,
    onError,
    isCarousel,
    slideNum,
  });
  const onResourceError = error => {
    onError({ type: 'resource', error });
  };
  const syncbakLoading = useSelector(state => state.syncbak.loading);

  return render({
    resource,
    onError: onResourceError,
    loading: syncbakLoading,
  });
};

PlayerResource.propTypes = {
  render: PropTypes.func,
  onError: PropTypes.func,
  videoToPlay: Video,
  videoToDisplay: Video,
  row: Row,
  isAutoplay: PropTypes.bool.isRequired,
  authorizedProvider: PropTypes.string,
  onSetResource: PropTypes.func,
  isCarousel: PropTypes.bool,
  slideNum: PropTypes.number,
};

PlayerResource.defaultProps = {
  render: () => null,
  onError: () => null,
  videoToPlay: null,
  videoToDisplay: null,
  row: null,
  authorizedProvider: null,
  onSetResource: () => null,
  isCarousel: false,
  slideNum: null,
};

export default PlayerResource;
